import PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';
import { brandHealth } from '@shopmy/universal';

import './BrandTrackingHealthVerdict.scss';
import { severityClassName } from '../../helpers/brand_health_chart_helpers';

const { rankBrandHealthStatus, worstBrandHealthStatus } = brandHealth;
const prettify = value => (value ? value.replace(/_/g, ' ').replace(/^./, character => character.toUpperCase()) : '');

// Union the detector's alert states with the raw error outcomes present in recent healthData, so a
// domain shows every failing family — including order errors that never crossed the alert threshold.
const buildDomainRows = (alertStates, errorsByDomain) => {
  const byDomain = {};
  const ensure = domain => (byDomain[domain] = byDomain[domain] || { domain, families: [], dataErrors: [] });
  for (const state of alertStates) ensure(state.domain).families.push(state);
  for (const [domain, errors] of Object.entries(errorsByDomain)) ensure(domain).dataErrors = errors;
  return Object.values(byDomain)
    .map(row => ({ ...row, worst: worstBrandHealthStatus(row.families.map(family => family.status)) }))
    .sort((a, b) => rankBrandHealthStatus(b.worst) - rankBrandHealthStatus(a.worst) || a.domain.localeCompare(b.domain));
};

const BrandTrackingHealthVerdict = props => {
  const { alertStates = [], errorsByDomain = {}, onSelectDomain } = props;
  if (!alertStates.length && !Object.keys(errorsByDomain).length) return null;
  const domains = buildDomainRows(alertStates, errorsByDomain);

  return (
    <div className='brand-tracking-health-verdict'>
      {domains.map(({ domain, families, dataErrors, worst }) => {
        // A stale row (status null) carries no current verdict: it is not an issue, and it cannot vouch for health.
        const brokenFamilies = families
          .filter(family => family.status && family.status !== 'OK')
          .sort((a, b) => rankBrandHealthStatus(b.status) - rankBrandHealthStatus(a.status));
        const verdictLabel = !worst ? 'Stale' : worst === 'OK' ? 'Healthy' : worst;
        const covered = new Set(brokenFamilies.map(family => `${family.family}__${family.issueType}`));
        const extraErrors = dataErrors.filter(error => !covered.has(`${error.family}__${error.outcome}`));
        return (
          <div key={domain} className='brand-tracking-health-verdict-domain'>
            <button type='button' className='brand-tracking-health-verdict-header' onClick={() => onSelectDomain?.(domain)}>
              <span className={cn('brand-tracking-health-status-dot', severityClassName(worst))} />
              <span className='brand-tracking-health-verdict-domain-name'>{domain}</span>
              <span className={cn('brand-tracking-health-verdict-status', severityClassName(worst))}>{verdictLabel}</span>
            </button>
            {(brokenFamilies.length > 0 || extraErrors.length > 0) && (
              <div className='brand-tracking-health-verdict-issues'>
                {brokenFamilies.map(family => (
                  <div key={`alert-${family.family}`} className='brand-tracking-health-verdict-issue'>
                    <span className='brand-tracking-health-verdict-family'>{prettify(family.family)}</span>
                    <span className='brand-tracking-health-verdict-sep'>—</span>
                    <span className='brand-tracking-health-verdict-detail'>{prettify(family.issueType) || 'Issue detected'}</span>
                    {family.statusSince && (
                      <span className='brand-tracking-health-verdict-since'>· since {moment(family.statusSince).fromNow()}</span>
                    )}
                  </div>
                ))}
                {extraErrors.map(error => (
                  <div key={`data-${error.family}-${error.outcome}`} className='brand-tracking-health-verdict-issue'>
                    <span className='brand-tracking-health-verdict-family'>{prettify(error.family)}</span>
                    <span className='brand-tracking-health-verdict-sep'>—</span>
                    <span className='brand-tracking-health-verdict-detail'>{prettify(error.outcome)}</span>
                    <span className='brand-tracking-health-verdict-count'>· {error.count.toLocaleString()} in recent events</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

BrandTrackingHealthVerdict.propTypes = {
  alertStates: PropTypes.array,
  errorsByDomain: PropTypes.object,
  onSelectDomain: PropTypes.func
};

export default BrandTrackingHealthVerdict;
